<template>
    <div class="flex flex-col max-w-lg border rounded-xl" :class="`bg-${props.color}-800 border-${props.color}-600`">
        <div
            v-if="result && result.message"
            class="px-4 py-3 -m-px text-sm font-semibold rounded-t-xl"
            :class="[result.success ? 'bg-green-300 text-green-800' : 'bg-red-300 text-red-800']"
        >
            <div v-if="result.result && result.result.payment && result.result.payment.admin_url">
                <a :href="result.result.payment.admin_url">
                    {{ result.message }}
                </a>
            </div>
            <div v-else-if="result.message">
                {{ result.message }}
            </div>
        </div>
        <header class="px-4 pt-3 pb-2">
            <h2 class="font-semibold text-brand-50">
                {{ title ?? lang('payments::payments.widgets.paid_in_cash.title') }}
            </h2>
            <p class="text-sm opacity-70 text-brand-200">
                {{ description ?? lang('payments::payments.widgets.paid_in_cash.description') }}
            </p>
        </header>
        <main>
            <div class="flex items-center justify-between px-4 py-3 text-brand-200">
                <form class="w-full forms__form" @submit.prevent="submit">
                    <div
                        class="p-2 text-sm"
                        :class="`bg-${props.color}-200 border-${props.color}-600 text-${props.color}-800`"
                    >
                        {{ lang('payments::payments.widgets.paid_in_cash.disclaimer') }}
                    </div>

                    <UiValidationErrors />

                    <div>
                        <Label for="paid-in-cash-amount" class="!text-brand-100">
                            {{ lang('payments::payments.widgets.paid_in_cash.amount') }}
                        </Label>
                        <AdminNumber
                            id="paid-in-cash-amount"
                            v-model="form.amount"
                            type="number"
                            class="text-brand-900"
                            required
                            :disabled="form.processing"
                            :class="{
                                'animate-pulse': form.processing,
                            }"
                        />
                    </div>
                    <div>
                        <Label for="paid-in-cash-meta-variable_symbol" class="!text-brand-100">
                            {{ lang('payments::payments.widgets.paid_in_cash.variable_symbol') }}
                        </Label>
                        <AdminText
                            id="paid-in-cash-meta-variable_symbol"
                            v-model="form.meta.variable_symbol"
                            placeholder="0000000075"
                            class="text-brand-900"
                            required
                            :disabled="form.processing"
                            :class="{
                                'animate-pulse': form.processing,
                            }"
                        />
                    </div>
                    <div>
                        <Label for="paid-in-cash-meta-insurance_first" class="!text-brand-100">
                            {{ lang('payments::payments.widgets.paid_in_cash.insurance_first') }}
                        </Label>
                        <AdminNumber
                            id="paid-in-cash-meta-insurance_first"
                            v-model="form.meta.insurance_first"
                            type="number"
                            class="text-brand-900"
                            required
                            :disabled="form.processing"
                            :class="{
                                'animate-pulse': form.processing,
                            }"
                        />
                    </div>
                    <div>
                        <Label for="paid-in-cash-meta-block_number" class="!text-brand-100">
                            {{ lang('payments::payments.widgets.paid_in_cash.block_number') }}
                        </Label>
                        <AdminText
                            id="paid-in-cash-meta-block_number"
                            v-model="form.meta.block_number"
                            class="text-brand-900"
                            :required="!props.optionalBlockNumber"
                            :disabled="form.processing"
                            :class="{
                                'animate-pulse': form.processing,
                            }"
                        />
                    </div>
                    <div
                        v-if="
                            $page.props.auth &&
                            $page.props.auth.user &&
                            $page.props.auth.user.teams &&
                            $page.props.auth.user.teams.find((item) => item.name === 'Admin') &&
                            props.createdFrom !== 'admin.index'
                        "
                    >
                        <div class="p-4 text-xs border rounded bg-white/10 border-white/40">
                            <Label for="paid-in-cash-allow_different_amount" class="!text-brand-100">
                                {{ lang('payments::payments.widgets.paid_in_cash.allow_different_amount') }}
                            </Label>
                            <AdminCheckbox
                                id="paid-in-cash-allow_different_amount"
                                v-model="form.allow_different_amount"
                            />
                        </div>
                    </div>
                    <div>
                        <Button
                            type="submit"
                            class="justify-center w-full rounded-lg"
                            :class="{ 'opacity-75': form.processing }"
                            :loading="form.processing"
                            :disabled="form.processing"
                            dusk="form-submit"
                            size="lg"
                            color="brand"
                        >
                            {{ lang('payments::payments.widgets.paid_in_cash.submit') }}
                        </Button>
                    </div>
                </form>
            </div>
        </main>
    </div>
</template>

<script setup lang="ts">
import { useForm, usePage } from '@inertiajs/vue3';
import { ref } from 'vue';

interface Props {
    optionalBlockNumber?: boolean;
    defaultBlockNumber?: string | null;
    title?: string | null;
    description?: string | null;
    color?: string;
    createdFrom?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
    optionalBlockNumber: false,
    defaultBlockNumber: null,
    title: null,
    description: null,
    color: 'brand',
    createdFrom: 'admin.index',
});

const form = useForm({
    provider: 'local',
    type: 'cash',
    provider_id: null,
    status: 'PAID',
    amount: null,
    currency: 'CZK',
    payment_link: null,
    meta: {
        variable_symbol: null,
        insurance_first: null,
        block_number: props.defaultBlockNumber,
    },
    created_from: props.createdFrom,
    allow_different_amount: false,
});

const result = ref(null);

const submit = () => {
    form.processing = true;
    axios
        .post(route('payments.admin.api.widgets.paid_in_cash'), form.data())
        .then(({ data }) => {
            form.reset();
            form.processing = false;

            usePage().props.errors = [];

            result.value = data;
        })
        .catch(({ response }) => {
            usePage().props.errors = response.data.errors;

            form.processing = false;
        });
};
</script>
